import {
  Alert,
  Badge,
  Center,
  Group,
  Loader,
  Pagination,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import dayjs from "dayjs";
import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import {
  DEFAULT_USER_FILTERS,
  type UserFilters,
  parseIsActiveParam,
  parseRoleFilter,
  parseTierFilter,
  parseUserFilters,
  userFiltersToSearchParams,
} from "@/lib/admin/filters";
import { useUpdateUser, useUsers } from "@/lib/admin/hooks";
import type { Role, Tier } from "@/lib/api/client";

const TIER_OPTIONS: { value: Tier; label: string }[] = [
  { value: "free", label: "Free" },
  { value: "premium", label: "Premium" },
];

const TIER_FILTER_OPTIONS = [{ value: "", label: "All tiers" }, ...TIER_OPTIONS];

const ROLE_FILTER_OPTIONS: { value: string; label: string }[] = [
  { value: "", label: "All roles" },
  { value: "user", label: "User" },
  { value: "admin", label: "Admin" },
];

const STATUS_FILTER_OPTIONS: { value: string; label: string }[] = [
  { value: "", label: "All statuses" },
  { value: "true", label: "Active" },
  { value: "false", label: "Inactive" },
];

function formatDate(iso: string): string {
  return dayjs(iso).format("YYYY-MM-DD");
}

function roleLabel(role: Role): string {
  return role === "admin" ? "Admin" : "User";
}

/** Admin users list with search, filters, pagination, and inline tier editing. */
export function UsersPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = parseUserFilters(searchParams);

  const [searchInput, setSearchInput] = useState(filters.query);
  const [debouncedSearch] = useDebouncedValue(searchInput, 300);

  const { data, isLoading, isError, error, isFetching } = useUsers(filters);
  const updateUser = useUpdateUser();

  const replaceFilters = useCallback(
    (next: UserFilters) => {
      setSearchParams(userFiltersToSearchParams(next), { replace: true });
    },
    [setSearchParams],
  );

  // Sync local search input when URL query changes externally (back/forward, deep link).
  useEffect(() => {
    setSearchInput(filters.query);
  }, [filters.query]);

  // Push debounced search to URL; reset to page 1 when query changes.
  useEffect(() => {
    if (debouncedSearch === filters.query) {
      return;
    }
    replaceFilters({
      ...filters,
      query: debouncedSearch,
      page: DEFAULT_USER_FILTERS.page,
    });
  }, [debouncedSearch, filters, replaceFilters]);

  const handleTierFilterChange = (value: string | null) => {
    replaceFilters({
      ...filters,
      tier: parseTierFilter(value),
      page: DEFAULT_USER_FILTERS.page,
    });
  };

  const handleRoleFilterChange = (value: string | null) => {
    replaceFilters({
      ...filters,
      role: parseRoleFilter(value),
      page: DEFAULT_USER_FILTERS.page,
    });
  };

  const handleStatusFilterChange = (value: string | null) => {
    replaceFilters({
      ...filters,
      isActive: parseIsActiveParam(value),
      page: DEFAULT_USER_FILTERS.page,
    });
  };

  const handlePageChange = (page: number) => {
    replaceFilters({ ...filters, page });
  };

  const handleTierChange = (userId: string, tier: Tier) => {
    updateUser.mutate({ userId, patch: { tier } });
  };

  const tierFilterValue = filters.tier ?? "";
  const roleFilterValue = filters.role ?? "";
  const statusFilterValue = filters.isActive === null ? "" : filters.isActive ? "true" : "false";

  return (
    <Stack gap="lg">
      <Group justify="space-between" align="flex-end">
        <Stack gap={4}>
          <Title order={2}>Users</Title>
          <Text c="dimmed" size="sm">
            Search and manage user accounts. Tier changes apply on the next API request.
          </Text>
        </Stack>
        {isFetching && !isLoading ? (
          <Text size="sm" c="dimmed">
            Refreshing…
          </Text>
        ) : null}
      </Group>

      <Group align="flex-end" wrap="wrap">
        <TextInput
          label="Search"
          placeholder="Email or display name"
          value={searchInput}
          onChange={(event) => setSearchInput(event.currentTarget.value)}
          style={{ flex: "1 1 240px", minWidth: 200 }}
        />
        <Select
          label="Tier"
          data={TIER_FILTER_OPTIONS}
          value={tierFilterValue}
          onChange={handleTierFilterChange}
          clearable={false}
          w={140}
        />
        <Select
          label="Role"
          data={ROLE_FILTER_OPTIONS}
          value={roleFilterValue}
          onChange={handleRoleFilterChange}
          clearable={false}
          w={140}
        />
        <Select
          label="Status"
          data={STATUS_FILTER_OPTIONS}
          value={statusFilterValue}
          onChange={handleStatusFilterChange}
          clearable={false}
          w={140}
        />
      </Group>

      {isLoading ? (
        <Center py="xl">
          <Loader size="lg" />
        </Center>
      ) : null}

      {isError ? (
        <Alert color="red" title="Failed to load users">
          {error instanceof Error ? error.message : "An unexpected error occurred."}
        </Alert>
      ) : null}

      {!isLoading && !isError && data ? (
        <>
          {data.items.length === 0 ? (
            <Text c="dimmed" ta="center" py="xl">
              No users match your filters.
            </Text>
          ) : (
            <Table.ScrollContainer minWidth={720}>
              <Table striped highlightOnHover withTableBorder withColumnBorders>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Email</Table.Th>
                    <Table.Th>Display name</Table.Th>
                    <Table.Th>Tier</Table.Th>
                    <Table.Th>Role</Table.Th>
                    <Table.Th>Status</Table.Th>
                    <Table.Th>Created</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {data.items.map((user) => {
                    const isRowUpdating =
                      updateUser.isPending && updateUser.variables?.userId === user.id;

                    return (
                      <Table.Tr key={user.id} data-testid={`user-row-${user.email}`}>
                        <Table.Td>
                          <Text
                            component={Link}
                            to={`/users/${user.id}`}
                            size="sm"
                            fw={500}
                            c="blue"
                          >
                            {user.email}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm" c={user.display_name ? undefined : "dimmed"}>
                            {user.display_name ?? "—"}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Select
                            data={TIER_OPTIONS}
                            value={user.tier}
                            onChange={(value) => {
                              if (value === "free" || value === "premium") {
                                handleTierChange(user.id, value);
                              }
                            }}
                            disabled={isRowUpdating}
                            size="xs"
                            w={120}
                            comboboxProps={{ withinPortal: true }}
                            data-testid={`tier-select-${user.email}`}
                          />
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">{roleLabel(user.role)}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Badge color={user.is_active ? "green" : "gray"} variant="light">
                            {user.is_active ? "Active" : "Inactive"}
                          </Badge>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">{formatDate(user.created_at)}</Text>
                        </Table.Td>
                      </Table.Tr>
                    );
                  })}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          )}

          {data.total_pages > 1 ? (
            <Group justify="center">
              <Pagination total={data.total_pages} value={data.page} onChange={handlePageChange} />
            </Group>
          ) : null}

          <Text size="sm" c="dimmed" ta="center">
            {data.total} user{data.total === 1 ? "" : "s"} total
          </Text>
        </>
      ) : null}
    </Stack>
  );
}
